// src/pages/materials/hooks/useMaterials.js
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api } from "@/api";

const DEBOUNCE = 300;

// ——— helpers ———
const toStr = (v) => (v == null ? undefined : String(v));

function normalizeItem(it) {
  const _id = toStr(it?._id ?? it?.id ?? it?.code);
  // Без случайных id: если _id нет — оставляем undefined (лучше исправить на бэке)
  const images = Array.isArray(it?.images) ? it.images : [];
  const normalized = {
    ...it,
    _id,
    id: _id, // единый ключ для всего фронта
    photo_url: it?.photo_url ?? images[0] ?? null,
    name: it?.name ?? "",
  };
  // Защита от внешних мутаций (в дев-режиме можно заморозить)
  // try { if (process.env.NODE_ENV !== 'production') Object.freeze(normalized); } catch {}
  return normalized;
}

export default function useMaterials() {
  const [raw, setRaw] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [group, setGroup] = useState(undefined);

  const [parents, setParents] = useState([]);
  const [childrenByParent, setChildrenByParent] = useState({});
  const [units, setUnits] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  // Гашим гонки ответов: применяем только последний вызов
  const reqSeqRef = useRef(0);
  const refsSeqRef = useRef(0);
    const detailAbortRef = useRef(null);

  /** ======= Загрузка списка (race‑proof) ======= */
  const fetchServer = useCallback(
    async (params = {}) => {
      const mySeq = ++reqSeqRef.current;
      setLoading(true);
      try {
        const { data } = await api.post("/materials/search", {
          q: params.q ?? query,
          group: params.group ?? group,
        });
        // Если прилетел устаревший ответ — игнорируем
        if (mySeq !== reqSeqRef.current) return;

        const items = Array.isArray(data?.items) ? data.items : [];
        const normalized = items.map(normalizeItem).filter((x) => x._id);

        setRaw(normalized);
        setTotal(Number.isFinite(data?.total) ? data.total : normalized.length);
      } catch (err) {
        if (mySeq === reqSeqRef.current) {
          console.error("[useMaterials] fetch error:", err);
        }
      } finally {
        if (mySeq === reqSeqRef.current) setLoading(false);
      }
    },
    [query, group]
  );


    const getById = useCallback(async (id) => {
    if (!id) return null;
    // отменяем предыдущий в полёте
    try { detailAbortRef.current?.abort(); } catch {}
    const controller = new AbortController();
    detailAbortRef.current = controller;

    const { data } = await api.get(`/materials/${id}`, { signal: controller.signal });
    // можно нормализовать, чтобы форма всегда получала однородный объект
    return normalizeItem(data);
  }, []);

  /** ======= Справочники (race‑proof) ======= */
  const fetchRefs = useCallback(async () => {
    const mySeq = ++refsSeqRef.current;
    try {
      const [catsRes, unitsRes, suppliersRes] = await Promise.allSettled([
        api.get("/material-categories"),
        api.get("/units"),
        api.get("/suppliers"),
      ]);
      if (mySeq !== refsSeqRef.current) return; // устаревший набор справочников

      const tree =
        catsRes.status === "fulfilled" && Array.isArray(catsRes.value?.data)
          ? catsRes.value.data
          : [];

      const parentList = tree.map((r) => ({ _id: toStr(r._id), name: r.name }));
      const map = Object.fromEntries(
        tree.map((r) => [
          toStr(r._id),
          (r.children || []).map((c) => ({ _id: toStr(c._id), name: c.name, parent_id: toStr(r._id) })),
        ])
      );

      const unitsData =
        unitsRes.status === "fulfilled" && Array.isArray(unitsRes.value?.data)
          ? unitsRes.value.data
          : [];
      const suppliersData =
        suppliersRes.status === "fulfilled" && Array.isArray(suppliersRes.value?.data)
          ? suppliersRes.value.data
          : [];

      setParents(parentList);
      setChildrenByParent(map);
      setUnits(unitsData);
      setSuppliers(suppliersData);
    } catch (e) {
      if (mySeq === refsSeqRef.current) console.error("[useMaterials] refs error:", e);
    }
  }, []);

  // Первичная загрузка
  useEffect(() => {
    fetchServer({ q: "", group: undefined });
    fetchRefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Обновление по запросу/группе c debounce и защитой от гонок
  useEffect(() => {
    const t = setTimeout(() => {
      fetchServer({ q: query, group });
    }, DEBOUNCE);
    return () => clearTimeout(t);
  }, [query, group, fetchServer]);

  /** ======= CRUD ======= */
  const create = useCallback(async (values) => {
    await api.post("/materials", values);
    await fetchServer({ q: query, group });
  }, [fetchServer, query, group]);

  const update = useCallback(async (id, values) => {
    // На всякий случай не отправляем _id в теле
    const { _id: _ignored, id: _ignored2, ...safe } = values || {};
    await api.put(`/materials/${id}`, safe);
    await fetchServer({ q: query, group });
  }, [fetchServer, query, group]);

  const remove = useCallback(async (id) => {
    await api.delete(`/materials/${id}`);
    await fetchServer({ q: query, group });
  }, [fetchServer, query, group]);

  /** ======= Локальная фильтрация (если нужна ещё сверх server‑side) ======= */
  const list = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return raw;
    return raw.filter((m) => {
      const bag = [
        m?.name,
        m?.group,
        m?.code,
        m?.color,
        m?.composition,
        m?.vendor,
        m?.supplier_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return bag.includes(q);
    });
  }, [raw, query]);

  /** ======= API хука ======= */
  return {
    list,
    total,
    loading,
    query, setQuery,
    group, setGroup,
    reload: () => fetchServer({ q: query, group }),
    create,
    update,
    remove,
    parents,
    childrenByParent,
    units,
    suppliers,
      getById,
  };
}
